import { SpiritLance } from '../systems/combat/weapons/SpiritLance.js';
import { ScatterCannon } from '../systems/combat/weapons/ScatterCannon.js';
import { ArcNeedle } from '../systems/combat/weapons/ArcNeedle.js';
import { OrbitWard } from '../systems/combat/weapons/OrbitWard.js';
import { NovaPulse } from '../systems/combat/weapons/NovaPulse.js';
import { Resonor } from '../systems/combat/weapons/Resonor.js';
import { UmbralLantern } from '../systems/combat/weapons/UmbralLantern.js';
import { Glyphpen } from '../systems/combat/weapons/Glyphpen.js';
import { Countersong } from '../systems/combat/weapons/Countersong.js';
import { TuningFork } from '../systems/combat/weapons/TuningFork.js';
import { ULTIMATES } from './ultimates.js';

/**
 * Playable roster. Everything here is applied once at run start (see Game.js's
 * constructor and _playerBonuses). `color` is the menu accent for the
 * champion's card/portrait and mirrors the sprite body color in
 * data/sprites.js PLAYER_SPRITES — keep the two in sync.
 *
 * Each champion is defined by three layers, not one:
 *
 *   passive    flat stat deltas (HP / speed / damage / dash range)
 *   signature  the always-on mechanic that makes them play differently —
 *              a distinct verb per champion (sustain a streak, hold a line,
 *              trade safety for burst, fight at arm's length, keep moving)
 *   ultimate   a charged, once-a-fight button (data/ultimates.js)
 *
 * The stat block is the smallest of the three on purpose. Passives alone made
 * every champion the same character with different numbers; the signature is
 * what a run actually feels like, and its config fields are read by the
 * systems that own each mechanic (Player for defense/streak state,
 * CombatSystem for anything that resolves at hit time).
 *
 * **`locked` / `unlockCost` — the Shard ladder.** The five founding champions
 * (Vess, Rook, Iris, Thorn, Kestrel) ship open: five distinct starting
 * weapons and five distinct signatures is already more variety than a new
 * player can use, and gating the on-ramp would be the opposite of the point.
 * The five added by the champion rework are bought with Shards, at
 * 60/90/120/160/210 — 640 in total, roughly eleven clears at Act I's rate.
 *
 * Every champion carried `locked: false` until 2026-07-27, which made
 * `unlockedCharacters`, `SaveManager.unlockCharacter` and CharacterSelect's
 * whole locked-card path dead code — the game shipped the ladder and then
 * disabled it. That mattered beyond tidiness: Shards' only sinks were a pet
 * gacha and a finite 280-Shard perk list, so ARCHIVE.md's "the economy caps
 * out" (gap 1) was partly self-inflicted, and the breadcrumb trail the genre
 * runs on — always a named next thing, one or two runs away — had nothing in
 * it that changes how a run plays.
 *
 * Existing saves are grandfathered (SaveManager MIGRATIONS[8]): anyone who has
 * finished a run keeps all ten. Taking a champion away from someone who has
 * been playing one is not a balance change, it is a bug with a rationale.
 */
export const CHARACTERS = [
  {
    id: 'vess',
    color: '#5ad1e6',
    name: 'Vess',
    title: 'The Wayfarer',
    description: 'Spirit Lance · +10% dash range',
    locked: false,
    createStartingWeapon: () => new SpiritLance(),
    passive: { dashRangeMultiplier: 1.1, moveSpeedMultiplier: 1, maxHpBonus: 0, damageMultiplier: 1 },
    signature: {
      name: 'Cadence',
      description: 'Each hit landed adds +4% fire rate, up to 12 stacks. They bleed off in a lull; taking a hit silences them outright.',
      // Consumed by Player (state + the decay clock) and read by CombatSystem
      // through player.cadenceFireRateMultiplier.
      cadence: { perStack: 0.04, maxStacks: 12, graceSeconds: 1.6, decayPerSecond: 4 },
    },
    ultimate: ULTIMATES.requiem,
  },
  {
    id: 'rook',
    color: '#7ad67a',
    name: 'Rook',
    title: 'The Last Wall',
    description: 'Scatter Cannon · +40 max HP · -15% move speed · shorter dash',
    locked: false,
    createStartingWeapon: () => new ScatterCannon(),
    passive: { dashRangeMultiplier: 0.9, moveSpeedMultiplier: 0.85, maxHpBonus: 40, damageMultiplier: 1 },
    signature: {
      name: 'Aegis Plating',
      description: '3 plates, each absorbing one incoming hit whole instead of losing HP. One rebuilds every 7s.',
      armorPlates: { max: 3, rechargeSeconds: 7 },
    },
    ultimate: ULTIMATES.bastion,
  },
  {
    id: 'iris',
    color: '#e07ae0',
    name: 'Iris',
    title: 'The Prodigy',
    description: 'Arc Needle · +10% damage · +5% move speed · -25 max HP',
    locked: false,
    createStartingWeapon: () => new ArcNeedle(),
    passive: { dashRangeMultiplier: 1.15, moveSpeedMultiplier: 1.05, maxHpBonus: -25, damageMultiplier: 1.1 },
    signature: {
      name: 'Killing Edge',
      description: '20% of hits crit for 2.4x, rising to 55% at death\'s door — the emptier the HP bar, the sharper the needle.',
      // Read by CombatSystem at hit time via player.critChance (Player folds
      // the missing-HP ramp into that getter).
      crit: { chance: 0.20, desperateChance: 0.55, multiplier: 2.4 },
    },
    ultimate: ULTIMATES.zeroHour,
  },
  {
    id: 'thorn',
    color: '#e0955a',
    name: 'Thorn',
    title: 'The Close Fighter',
    description: 'Orbit Ward · +10 max HP · -10% move speed',
    locked: false,
    createStartingWeapon: () => new OrbitWard(),
    passive: { dashRangeMultiplier: 1, moveSpeedMultiplier: 0.9, maxHpBonus: 10, damageMultiplier: 1 },
    signature: {
      name: 'Briar Aura',
      description: 'An aura ticks 8 dmg/sec to anything inside it, and Thorn\'s own hits land +35% harder in that ring.',
      aura: { radius: 34, damagePerSecond: 8, closeRangeBonus: 0.35 },
    },
    ultimate: ULTIMATES.thornsong,
  },
  {
    id: 'kestrel',
    color: '#ffd166',
    name: 'Kestrel',
    title: 'The Striker',
    description: 'Nova Pulse · 2x dash range · dash strike · -10 max HP',
    locked: false,
    createStartingWeapon: () => new NovaPulse(),
    // 2× dash distance (distance is dashRange·dashRangeMultiplier,
    // so doubling the multiplier doubles the travel while keeping i-frame timing).
    passive: { dashRangeMultiplier: 2, moveSpeedMultiplier: 1, maxHpBonus: -10, damageMultiplier: 1 },
    // Opt-in field CombatSystem checks for (see CombatSystem._checkDashAttack)
    // — only characters that define this get bonus damage from dashing into
    // an enemy, on top of their normal weapon fire. Landing it also recoils
    // the player back (CombatSystem._applyDashAoe) with shake/hit-stop juice.
    dashAttack: { damage: 60, radius: 22 },
    signature: {
      name: 'Slipstream',
      description: 'A landed dash strike refunds the dash and stacks +25% strike damage (max 4). Stall 3s and the chain drops.',
      dashChain: { perStack: 0.25, maxStacks: 4, window: 3, refundCooldown: 0.8 },
    },
    ultimate: ULTIMATES.skybreaker,
  },
  // --- Act II (ACT2-BIBLE.md §6) --------------------------------------------
  // Two verbs the first five don't cover: carry a kill outward, and endure and
  // recover. Both are roster content available from run start in every mode —
  // neither depends on reaching Act II.
  {
    id: 'aria',
    color: '#c9a0ff',
    name: 'Aria',
    title: 'The Cantor',
    description: 'Resonor · +5% move speed · -10 max HP',
    locked: true,
    unlockCost: 60,
    createStartingWeapon: () => new Resonor(),
    passive: { dashRangeMultiplier: 1, moveSpeedMultiplier: 1.05, maxHpBonus: -10, damageMultiplier: 1 },
    signature: {
      name: 'Refrain',
      description: 'Aria\'s damage echoes into whatever she hits. When it dies the echo carries to everything nearby, and what that kills carries it again.',
      // Owned entirely by CombatSystem (banked at hit time in _bankRefrain,
      // released in _resolveRefrain) — no Player state and no clock, so every
      // other champion pays one null check for it.
      //
      // The verb nobody else has: propagation. Vess's Cadence and Iris's
      // Killing Edge both want one target held; Refrain wants a crowd, and
      // pays nothing in a bare boss room where there is nothing to carry to.
      // Only non-boss deaths release an echo (see _resolveRefrain) — a boss's
      // banked share of a health bar that large would detonate for absurd
      // numbers, and "weakest against a lone boss" is the tradeoff.
      refrain: { echoFraction: 0.45, radius: 90, chainFalloff: 0.6, maxChain: 3 },
    },
    ultimate: ULTIMATES.tutti,
  },
  {
    id: 'cairn',
    color: '#8a9bb0',
    name: 'Cairn',
    title: 'The Undying',
    description: 'Umbral Lantern · +50 max HP · -10% move speed',
    locked: true,
    unlockCost: 90,
    createStartingWeapon: () => new UmbralLantern(),
    passive: { dashRangeMultiplier: 1, moveSpeedMultiplier: 0.9, maxHpBonus: 50, damageMultiplier: 1 },
    signature: {
      name: 'Debt',
      description: 'Damage taken is banked, not just spent. Go 4s unhit and the deep pays it back as healing.',
      // Owned entirely by Player (banked in takeDamage, repaid in update) —
      // the opposite pull from Iris\'s low-HP gamble and Rook\'s prevention:
      // take the hit, then play clean to earn it back.
      debt: { bankFraction: 0.6, repayAfter: 4.0, repayRate: 14 },
    },
    ultimate: ULTIMATES.secondSilence,
  },
  // --- Act III (ACT3-BIBLE.md §6) -------------------------------------------
  // The roster's nine verbs before these two: sustain a streak (Vess), hold a
  // line (Rook), trade safety for burst (Iris), fight at arm's length (Thorn),
  // keep moving (Kestrel), carry a kill outward (Aria), endure and recover
  // (Cairn). These add LEAVE THINGS BEHIND and BE TWO BUILDS — both absent, and
  // both, fittingly for an act about authorship, about what you put down and
  // what answers you. Roster content available from run start in every mode;
  // neither depends on reaching Act III.
  {
    id: 'quill',
    // The Ostinato's gold is Kestrel's exactly, and two champions can't share a
    // menu accent — this is that gold pulled toward ink/amber, so Quill still
    // reads as the act's colour without colliding on the select screen.
    color: '#e0a63c',
    name: 'Quill',
    title: 'The Author',
    description: 'Glyphpen · +5% damage · -15 max HP',
    locked: true,
    unlockCost: 120,
    createStartingWeapon: () => new Glyphpen(),
    passive: { dashRangeMultiplier: 1, moveSpeedMultiplier: 1, maxHpBonus: -15, damageMultiplier: 1.05 },
    signature: {
      name: 'Inscription',
      description: 'Stand still for a moment and a glyph is written at your feet. Glyphs pulse damage for 10s, and standing on one speeds your fire.',
      // Owned by Player (the glyphs, the write timer, the standing bonus) and
      // CombatSystem (their damage pulse) — the same split every signature uses.
      //
      // BALANCE WATCH (ACT3-BIBLE §6): a stand-still champion in a bullet-hell
      // is a contradiction by design, and `writeTime` is the single knob that
      // decides whether it reads as tension or as unplayable. It is short
      // enough that a gap in a pattern is a writing window, and long enough
      // that placing a glyph is a real decision. Move this before anything else
      // if Quill tests badly in either direction.
      inscription: {
        writeTime: 0.6,
        glyphLifetime: 10,
        glyphRadius: 26,
        maxGlyphs: 4,
        pulseInterval: 0.5,
        pulseDamage: 11,
        standingFireBonus: 0.25,
        // Fair Copy only (data/ultimates.js): the tether between glyphs.
        chainRadius: 12,
        chainDamage: 7,
      },
    },
    ultimate: ULTIMATES.fairCopy,
  },
  {
    id: 'fugue',
    color: '#66d9ff',
    name: 'Fugue',
    title: 'The Two Songs',
    description: 'Countersong · +5% move speed · press F to swap stance',
    locked: true,
    unlockCost: 160,
    createStartingWeapon: () => new Countersong(),
    passive: { dashRangeMultiplier: 1, moveSpeedMultiplier: 1.05, maxHpBonus: 0, damageMultiplier: 1 },
    signature: {
      name: 'Countersubject',
      description: 'Two stances (F): Statement fires faster but takes more; Answer mitigates and bites at contact range. Each swap leaves the stance you left running for a moment.',
      // Owned by Player (the stances, the cooldown, the echo) and CombatSystem
      // (Answer's close-range bite). The echo is what makes this a timing
      // mechanic rather than a menu: for a beat after every swap you have both,
      // so mastery is WHEN you swap, not which stance you picked.
      stances: {
        swapCooldown: 4.0,
        swapEchoSeconds: 1.2,
        statementFireBonus: 0.35,
        statementExposure: 0.2, // taken as NEGATIVE damage reduction — see Player.takeDamage
        answerDamageReduction: 0.25,
        answerRange: 46,
        answerCloseBonus: 0.4,
      },
    },
    ultimate: ULTIMATES.stretto,
  },
  // --- The polarity champion (BULLET-HELL.md §D7) ---------------------------
  // §D7 built the polarity payoff and left its better half open: as an ACT
  // rule, absorption is a gimmick one boss-set wide; as a CHAMPION SIGNATURE
  // it is a whole alternate way to play every fight in the game. This is that
  // champion. Roster content available from run start in every mode — she does
  // not depend on reaching Act II, which is the entire point of her.
  {
    id: 'tine',
    // A tine is the prong of a tuning fork: the thing that is struck, and the
    // reason there are two of them. Rose — the roster's warm hues are Thorn's
    // clay, Kestrel's gold and Quill's amber, and its violets are Aria's and
    // Iris's orchid, so this sits between both families without colliding with
    // either. Deliberately NOT the polarity violet (POLARITY.color): that
    // belongs to the resonance readout, and a champion whose accent was her own
    // meter would make the one thing she has to read illegible.
    color: '#ff6fae',
    name: 'Tine',
    title: 'The Struck Note',
    description: 'Tuning Fork · -20 max HP · press F to flip attunement',
    locked: true,
    unlockCost: 210,
    createStartingWeapon: () => new TuningFork(),
    // The lowest HP on the roster alongside Iris, for the same kind of reason:
    // half of all incoming boss fire is hers to swim through, and the other
    // half bites harder. A champion who edits the damage she takes that much
    // cannot also carry a normal bar.
    passive: { dashRangeMultiplier: 1, moveSpeedMultiplier: 1, maxHpBonus: -20, damageMultiplier: 1 },
    signature: {
      name: 'Attunement',
      description: 'Every boss bullet takes a side, warm or cold. Your tone washes through you and pays; the other bites for half again. F flips it.',
      // Split three ways, all of it §D7 machinery that already existed:
      //   the stamp   systems/combat/attunement.js, on every BulletPool she
      //               fights (Game._attachToneCaster)
      //   the flip    Player.flipAttunement (the F key) — the dash still flips
      //               too, so a dash through a wall remains a flip you paid for
      //   the income  Resonance, unchanged, except that it now has bullets to
      //               count in every act instead of only in the Antiphon's
      //
      // The verb nobody else has: CHOOSING WHICH HALF OF THE SCREEN IS REAL.
      // Every other champion answers bullets by not being where they are.
      //
      // BALANCE WATCH: `mismatchBonus` is the whole trade. At 0.5 (the
      // Antiphon's own number) a misread costs more than the match saves, and
      // she is a glass champion who must read every ring; below ~0.3 the
      // signature collapses into "take 35% less damage from bosses" and no
      // flip has a wrong answer. Move this before anything else if she tests
      // strong or weak — not the HP, which is only the tax on the trade.
      attune: {
        warmColor: '#ff8a4d',
        coldColor: '#4dc9ff',
        mismatchBonus: 0.45,
        // A hair over a fifth of a second. Free flipping is strictly better
        // than reading (hold the key, be hit by nothing), so the cooldown is
        // what keeps the read a read; it is short enough that a misread is
        // recoverable one bullet later rather than a death sentence.
        flipCooldown: 0.22,
        // Her fuse is shorter than POLARITY.stacksPerRelease's 12: that number
        // is sized so one cleanly-read Twin Voice ring earns a release in the
        // one fight that has them, whereas she is reading tones all run and a
        // 12-absorb bank would make the release the rare event instead of the
        // rhythm. Read by Resonance via Game.
        stacksPerRelease: 9,
      },
    },
    ultimate: ULTIMATES.unison,
  },
];
